import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import {
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  Typography,
  CircularProgress,
  Snackbar,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { useQuery, useMutation, useQueryClient } from 'react-query'

import useGetLocalStorage from '../../hooks/useGetLocalStorage'
import hasPermission from '../../utils/helpers/hasPermission'
import USER_PERMISSIONS from '../../utils/constants/userPermissions'
import localStorageIds from '../../utils/constants/localStorageIds'
import { EVENT_TYPE_FORMS } from '../../utils/constants/eventTypeForms'
import { setLocalStorageValue, removeLocalStorageValue } from '../../utils/helpers/localStorageHelperMethods'
import QueryKeys from '../../utils/constants/queryKeys'

import getCompetitions from '../../Apis/competition/getCompetitions'
import getCompetition from '../../Apis/competition/getCompetition'
import deleteCompetition from '../../Apis/competition/deleteCompetition'
import getStagesWithStandings from '../../Apis/stages/getStagesWithStandings'
import getGamesByContext from '../../Apis/event/getGamesByContext'
import attachGamesToStages from '../../utils/helpers/attachGamesToStages'
import deleteStage from '../../Apis/stages/deleteStage'
import reorderStages from '../../Apis/stages/reorderStages'
import deleteGroup from '../../Apis/groups/deleteGroup'
import removeTeamFromGroup from '../../Apis/groups/removeTeamFromGroup'
import reorderGroups from '../../Apis/groups/reorderGroups'
import deleteGameday from '../../Apis/gamedays/deleteGameday'
import reorderGamedays from '../../Apis/gamedays/reorderGamedays'
import deleteSeason from '../../Apis/season/deleteSeason'
import refreshSeasonSync from '../../Apis/season/refreshSeasonSync'
import getSeasonTeams from '../../Apis/season/getSeasonTeams'
import removeTeamFromSeason from '../../Apis/season/removeTeamFromSeason'
import updateSeasonTeam from '../../Apis/season/updateSeasonTeam'

import { TeamCalendarProvider, useTeamCalendarContext } from '../../context/useTeamCalendarContext'
import { useCmsScope } from '../../context/useCmsScope'
import useUrlSearchParams from '../../hooks/useUrlSearchParams'
import usePairedCompetitions from '../../hooks/usePairedCompetitions'
import teamCompetitionsOf from '../../utils/helpers/teamCompetitionsOf'
import CreateEventDrawer from '../Calendar/CreateEventDrawer'
import EditGamedayModal from './GamedaysTab/EditGamedayModal/EditGamedayModal'
import BulkAddGamedaysModal from './GamedaysTab/BulkAddGamedaysModal/BulkAddGamedaysModal'
import AssignTeamDropdown from './TeamsTab/AssignTeamDropdown/AssignTeamDropdown'
import EditSeasonTeamModal from './TeamsTab/EditSeasonTeamModal/EditSeasonTeamModal'
import CalendarEventClickModal from '../Calendar/CalendarEventClickModal/CalendarEventClickModal'

import TOKENS from './redesign/tokens'
import TournamentTree from './redesign/TournamentTree'
import { CompetitionsView, CompetitionView } from './redesign/CompetitionViews'
import SeasonView from './redesign/SeasonView'
import { StageView, GroupView, GamedayView } from './redesign/DeepViews'
import { StageSlideOver, GroupSlideOver } from './redesign/forms/StageGroupSlideOvers'
import { SeasonSlideOver, CompetitionSlideOver } from './redesign/forms/SeasonCompetitionSlideOvers'

const useStyles = makeStyles({
  shell: {
    display: 'flex',
    height: 'calc(100dvh - 65px)',
    background: TOKENS.surface,
    color: TOKENS.text,
    fontFamily: TOKENS.font,
  },
  main: { flex: 1, minWidth: 0, overflowY: 'auto' },
  loader: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' },
})

const normalizeSeasonTeam = (t) => ({
  seasonTeamId: t.id,
  teamId: t.teamId,
  teamName: t.teamName,
  teamLogoUrl: t.teamLogoUrl,
})

// Reorder an array of {id} objects to match the given id order (extras appended).
const reorderById = (arr = [], orderedIds = []) => {
  const byId = new Map(arr.map((x) => [x.id, x]))
  const out = orderedIds.map((id) => byId.get(id)).filter(Boolean)
  arr.forEach((x) => { if (!orderedIds.includes(x.id)) out.push(x) })
  return out
}

const CmsAdminPageContent = () => {
  const classes = useStyles()
  const queryClient = useQueryClient()
  const history = useHistory()
  const location = useLocation()
  // Read-only: a club browsing the federation competition its team is paired into.
  const { orgId, storageId, readOnly, competitionId } = useCmsScope()
  const localStorageValue = useGetLocalStorage()
  const storedCompetition = localStorageValue?.[storageId(localStorageIds.SELECTED_COMPETITION)]
    || (competitionId ? { id: competitionId } : null)
  const storedSeason = localStorageValue?.[storageId(localStorageIds.SELECTED_SEASON)] || null
  const storedStage = localStorageValue?.[storageId(localStorageIds.SELECTED_STAGE)] || null
  const storedGroup = localStorageValue?.[storageId(localStorageIds.SELECTED_GROUP)] || null

  // Restore the full drill-down the operator left off on (competition → season → stage →
  // group), not just the competition. The sync effects below reconcile these stale stored
  // snapshots against the freshly fetched data (and drop any that no longer exist).
  const [selectedCompetition, setSelectedCompetition] = useState(storedCompetition)
  const [selectedSeason, setSelectedSeason] = useState(storedSeason)
  const [selectedStage, setSelectedStage] = useState(storedStage)
  const [selectedGroup, setSelectedGroup] = useState(storedGroup)
  const [selectedGameday, setSelectedGameday] = useState(null)
  // Land on the seasons list only when a competition is stored but nothing deeper is;
  // otherwise drop straight into the stored season/stage/group.
  // A paired club opening its competition the first time goes straight to the active season.
  const [showSeasonsList, setShowSeasonsList] = useState(Boolean(storedCompetition) && !storedSeason && !readOnly)
  const [seasonTab, setSeasonTab] = useState('stages')

  // Slide-over / modal state
  const [stageForm, setStageForm] = useState(null) // { stage? }
  const [groupForm, setGroupForm] = useState(null) // { group?, stageId }
  const [competitionForm, setCompetitionForm] = useState(null) // { competition? } | 'new'
  const [gamedayBulk, setGamedayBulk] = useState(null) // { group }
  const [gamedayEdit, setGamedayEdit] = useState(null) // { group, gameday }
  const [seasonAdd, setSeasonAdd] = useState(false)
  const [seasonEdit, setSeasonEdit] = useState(null) // season
  const [editingTeam, setEditingTeam] = useState(null) // season team
  const [confirm, setConfirm] = useState(null) // { title, message, onConfirm }

  const { setCreateEventDrawerOpen, setGamedayId, setClickedEventId, setClickedEventOrgId, setCoordinatesModal, setDates, setSelectedEventType, eventTypes, deleteEvent, createEventDrawerOpen } =
    useTeamCalendarContext()

  // Clicking a game card opens the shared event popover (Watch Video / View Stats /
  // Get Recording / YouTube link), the same one the calendar uses.
  const [eventClickModalOpen, setEventClickModalOpen] = useState(false)

  // The popover resolves its event through the calendar context's getEvent, which is
  // gated on getAllEvents having loaded. Seed a broad date window on mount so that query
  // runs for the current org/season/group and the event type can be resolved.
  useEffect(() => {
    const now = new Date()
    setDates({ start: new Date(now.getFullYear() - 1, 0, 1), end: new Date(now.getFullYear() + 1, 11, 31) })
  }, [setDates])

  const openGameClickModal = (gameId, e) => {
    if (e) setCoordinatesModal({ x: e.clientX, y: e.clientY })
    setClickedEventOrgId(readOnly ? orgId : null)
    setClickedEventId(gameId)
    setEventClickModalOpen(true)
  }

  const seasonId = selectedSeason?.id

  /* ----------------------------------------------------------- data ------- */

  // Reads are user-driven, not flaky: a 4xx means "wrong/stale id", so don't retry it
  // (react-query's default of 3 retries turns one bad id into 4 requests + 4 toasts).
  const cmsRetry = (failureCount, error) => {
    const status = error?.response?.status
    if (status >= 400 && status < 500) return false
    return failureCount < 1
  }

  const { data: federationCompetitions, isLoading: isLoadingFederationCompetitions } = useQuery(
    [QueryKeys.GET_COMPETITIONS, orgId],
    () => getCompetitions(orgId),
    { enabled: Boolean(orgId), retry: cmsRetry },
  )

  // A paired club sees the competitions its selected team plays in, whatever else the caller may
  // read in the federation. Undefined until the team's pairings load, so a stored selection isn't
  // dropped meanwhile.
  const { org: clubOrgId, team: clubTeamId } = useUrlSearchParams(['org', 'team']) || {}
  const { data: teamCompetitions, isLoading: isLoadingTeamCompetitions } = usePairedCompetitions(clubOrgId, clubTeamId, { enabled: readOnly })
  const competitions = useMemo(
    () => (readOnly ? teamCompetitionsOf(federationCompetitions, teamCompetitions, orgId) : federationCompetitions),
    [readOnly, federationCompetitions, teamCompetitions, orgId],
  )
  const isLoadingCompetitions = isLoadingFederationCompetitions || (readOnly && isLoadingTeamCompetitions)

  // Only fetch the detail once we know the selected competition actually belongs to the
  // current org (it's in the loaded list). Otherwise a stale competition persisted in
  // localStorage from a previous org would 404.
  const competitionInOrg = Boolean(selectedCompetition?.id && competitions?.some((c) => c.id === selectedCompetition.id))
  const { data: competitionDetail, isLoading: isLoadingCompetitionDetail } = useQuery(
    [QueryKeys.GET_COMPETITION, orgId, selectedCompetition?.id],
    () => getCompetition({ orgId, competitionId: selectedCompetition?.id }),
    { enabled: Boolean(orgId) && competitionInOrg, retry: cmsRetry },
  )

  // Drop a stale selection (e.g. after an org switch) so we don't operate on another
  // org's competition/season and spray 404s.
  useEffect(() => {
    if (competitions && selectedCompetition && !competitionInOrg) goCompetitions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitions, competitionInOrg])

  // Switching org must clear the previous org's selection immediately — before its stale
  // competition/season ids get queried against the new org. (Skips the initial mount so a
  // validly-stored competition is still restored.)
  const prevOrgId = useRef(orgId)
  useEffect(() => {
    if (prevOrgId.current !== orgId) {
      prevOrgId.current = orgId
      goCompetitions()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  const competitionSeasons = useMemo(() => competitionDetail?.seasons || [], [competitionDetail])

  // Auto-select / sync season when competition detail loads. Seasons come newest first; the
  // active one is where the operator works, so open on it when there is one.
  useEffect(() => {
    if (competitionSeasons.length === 0) return
    const fallback = competitionSeasons.find((s) => s.active) || competitionSeasons[0]
    if (!selectedSeason) {
      setSelectedSeason(fallback)
      setLocalStorageValue([{ id: storageId(localStorageIds.SELECTED_SEASON), value: fallback }])
    } else {
      const updated = competitionSeasons.find((s) => s.id === selectedSeason.id) || fallback
      setSelectedSeason(updated)
      setLocalStorageValue([{ id: storageId(localStorageIds.SELECTED_SEASON), value: updated }])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionSeasons])

  // Season-level reads only make sense under a valid in-org competition. Gating on
  // competitionInOrg also closes the org-switch window where a stale season id would
  // otherwise be queried against the new org (stages returns another org's data, and
  // season-teams 404s because it validates org ownership).
  const { data: rawStages = [], isLoading: isLoadingStages } = useQuery(
    [QueryKeys.GET_STAGES, orgId, seasonId],
    () => getStagesWithStandings({ orgId, seasonId }),
    { enabled: Boolean(orgId && seasonId) && competitionInOrg, retry: cmsRetry },
  )

  // Games come from the dedicated by-context endpoint (no longer embedded in /stages) and
  // are merged back onto the tree by gamedayId, so all downstream views read gameday.games.
  const { data: games = [] } = useQuery(
    [QueryKeys.GET_GAMES_BY_CONTEXT, orgId, seasonId],
    () => getGamesByContext({ orgId, seasonId }),
    { enabled: Boolean(orgId && seasonId) && competitionInOrg, retry: cmsRetry },
  )
  const stages = useMemo(() => attachGamesToStages(rawStages, games), [rawStages, games])

  const { data: seasonTeamsRaw = [], isLoading: isLoadingSeasonTeams } = useQuery(
    [QueryKeys.GET_SEASON_TEAMS, orgId, seasonId],
    () => getSeasonTeams(orgId, seasonId),
    { enabled: Boolean(orgId && seasonId) && competitionInOrg, retry: cmsRetry },
  )
  const seasonTeams = useMemo(() => (seasonTeamsRaw || []).map(normalizeSeasonTeam), [seasonTeamsRaw])

  // Keep deep selections in sync with refreshed stages data (and drop stale ones)
  useEffect(() => {
    if (!stages) return
    if (selectedStage) {
      const st = stages.find((s) => s.id === selectedStage.id)
      if (!st) {
        setSelectedStage(null); setSelectedGroup(null); setSelectedGameday(null); return
      }
      if (st !== selectedStage) setSelectedStage(st)
      if (selectedGroup) {
        const g = (st.groups || []).find((x) => x.id === selectedGroup.id)
        if (!g) {
          setSelectedGroup(null); setSelectedGameday(null); return
        }
        if (g !== selectedGroup) setSelectedGroup(g)
        if (selectedGameday) {
          const gd = (g.gamedays || []).find((x) => x.id === selectedGameday.id)
          if (!gd) setSelectedGameday(null)
          else if (gd !== selectedGameday) setSelectedGameday(gd)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stages])

  const invalidateStages = () => queryClient.invalidateQueries([QueryKeys.GET_STAGES, orgId, seasonId])
  const invalidateGames = () => queryClient.invalidateQueries([QueryKeys.GET_GAMES_BY_CONTEXT, orgId, seasonId])

  // Refresh games when the create/edit game drawer closes (a game may have been saved).
  const prevDrawerOpen = useRef(false)
  useEffect(() => {
    if (prevDrawerOpen.current && !createEventDrawerOpen) invalidateGames()
    prevDrawerOpen.current = createEventDrawerOpen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createEventDrawerOpen])

  /* -------------------------------------------------------- mutations ----- */

  const closeConfirm = () => setConfirm(null)
  const onDeleteSuccess = (extra) => () => { closeConfirm(); if (extra) extra() }

  const deleteCompetitionMutation = useMutation((competitionId) => deleteCompetition({ orgId, competitionId }), {
    onSuccess: onDeleteSuccess(() => {
      queryClient.invalidateQueries([QueryKeys.GET_COMPETITIONS, orgId])
      goCompetitions()
    }),
  })
  const deleteSeasonMutation = useMutation((sId) => deleteSeason(orgId, sId), {
    onSuccess: onDeleteSuccess(() => {
      setSelectedSeason(null)
      queryClient.invalidateQueries([QueryKeys.GET_COMPETITION, orgId, selectedCompetition?.id])
    }),
  })
  const deleteStageMutation = useMutation((stageId) => deleteStage({ orgId, stageId }), {
    onSuccess: onDeleteSuccess(() => { setSelectedStage(null); setSelectedGroup(null); setSelectedGameday(null); invalidateStages() }),
  })
  const deleteGroupMutation = useMutation((groupId) => deleteGroup({ orgId, groupId }), {
    onSuccess: onDeleteSuccess(() => { setSelectedGroup(null); setSelectedGameday(null); invalidateStages() }),
  })
  const deleteGamedayMutation = useMutation((gamedayId) => deleteGameday({ orgId, gamedayId }), {
    onSuccess: onDeleteSuccess(() => { setSelectedGameday(null); invalidateStages() }),
  })
  const removeFromSeasonMutation = useMutation((seasonTeamId) => removeTeamFromSeason({ orgId, seasonTeamId }), {
    onSuccess: () => queryClient.invalidateQueries([QueryKeys.GET_SEASON_TEAMS, orgId, seasonId]),
  })
  const removeFromGroupMutation = useMutation(
    ({ groupId, seasonTeamId }) => removeTeamFromGroup({ orgId, groupId, seasonTeamId }),
    { onSuccess: () => { invalidateStages(); queryClient.invalidateQueries(QueryKeys.GET_GROUP_TEAMS) } },
  )
  const updateSeasonTeamMutation = useMutation(
    ({ seasonTeamId, name }) => updateSeasonTeam({ orgId, seasonTeamId, name }),
    { onSuccess: () => { queryClient.invalidateQueries([QueryKeys.GET_SEASON_TEAMS, orgId, seasonId]); setEditingTeam(null) } },
  )
  // Manual CMS refresh. The backend responds 202 and runs the sync in the background, so
  // there's nothing to invalidate yet — just confirm the job was enqueued. Errors surface
  // via the shared axios error toast.
  const [syncStartedOpen, setSyncStartedOpen] = useState(false)
  const refreshSeasonSyncMutation = useMutation((sId) => refreshSeasonSync(orgId, sId), {
    onSuccess: () => setSyncStartedOpen(true),
  })

  /* ------------------------------------------------------- reordering ----- */
  // Optimistically reorder the stages-tree cache, then persist. On error, refetch to
  // roll back to the server's truth.
  const stagesKey = [QueryKeys.GET_STAGES, orgId, seasonId]
  const reorderStagesMutation = useMutation((orderedIds) => reorderStages({ orgId, seasonId, orderedIds }), { onError: invalidateStages })
  const reorderGroupsMutation = useMutation(({ stageId, orderedIds }) => reorderGroups({ orgId, stageId, orderedIds }), { onError: invalidateStages })
  const reorderGamedaysMutation = useMutation(({ groupId, orderedIds }) => reorderGamedays({ orgId, groupId, orderedIds }), { onError: invalidateStages })

  const reorderStagesAction = (orderedIds) => {
    queryClient.setQueryData(stagesKey, (old = []) => reorderById(old, orderedIds))
    reorderStagesMutation.mutate(orderedIds)
  }
  const reorderGroupsAction = (stageId, orderedIds) => {
    queryClient.setQueryData(stagesKey, (old = []) =>
      old.map((s) => (s.id === stageId ? { ...s, groups: reorderById(s.groups || [], orderedIds) } : s)))
    reorderGroupsMutation.mutate({ stageId, orderedIds })
  }
  const reorderGamedaysAction = (groupId, orderedIds) => {
    queryClient.setQueryData(stagesKey, (old = []) =>
      old.map((s) => ({
        ...s,
        groups: (s.groups || []).map((g) => (g.id === groupId ? { ...g, gamedays: reorderById(g.gamedays || [], orderedIds) } : g)),
      })))
    reorderGamedaysMutation.mutate({ groupId, orderedIds })
  }

  const anyDeleteLoading =
    deleteCompetitionMutation.isLoading || deleteSeasonMutation.isLoading || deleteStageMutation.isLoading ||
    deleteGroupMutation.isLoading || deleteGamedayMutation.isLoading || removeFromSeasonMutation.isLoading ||
    removeFromGroupMutation.isLoading

  /* ------------------------------------------------------- navigation ----- */

  function goCompetitions() {
    setSelectedCompetition(null); setSelectedSeason(null); setSelectedStage(null)
    setSelectedGroup(null); setSelectedGameday(null); setShowSeasonsList(false)
    removeLocalStorageValue([storageId(localStorageIds.SELECTED_COMPETITION), storageId(localStorageIds.SELECTED_STAGE), storageId(localStorageIds.SELECTED_GROUP)])
  }
  const openCompetition = (comp) => {
    setSelectedCompetition(comp)
    setLocalStorageValue([{ id: storageId(localStorageIds.SELECTED_COMPETITION), value: comp }])
    removeLocalStorageValue([storageId(localStorageIds.SELECTED_STAGE), storageId(localStorageIds.SELECTED_GROUP)])
    setSelectedSeason(null); setSelectedStage(null); setSelectedGroup(null); setSelectedGameday(null)
    setShowSeasonsList(true); setSeasonTab('stages')
  }
  const openSeasonObj = (season) => {
    setSelectedSeason(season)
    if (season) setLocalStorageValue([{ id: storageId(localStorageIds.SELECTED_SEASON), value: season }])
    removeLocalStorageValue([storageId(localStorageIds.SELECTED_STAGE), storageId(localStorageIds.SELECTED_GROUP)])
    setSelectedStage(null); setSelectedGroup(null); setSelectedGameday(null)
    setShowSeasonsList(false); setSeasonTab('stages')
  }
  const backToSeason = () => {
    removeLocalStorageValue([storageId(localStorageIds.SELECTED_STAGE), storageId(localStorageIds.SELECTED_GROUP)])
    setSelectedStage(null); setSelectedGroup(null); setSelectedGameday(null); setShowSeasonsList(false)
  }
  const openStage = (stage) => {
    setLocalStorageValue([{ id: storageId(localStorageIds.SELECTED_STAGE), value: stage }])
    removeLocalStorageValue([storageId(localStorageIds.SELECTED_GROUP)])
    setSelectedStage(stage); setSelectedGroup(null); setSelectedGameday(null); setShowSeasonsList(false)
  }
  // The shared event form (EventTeamsFields) reads SELECTED_GROUP from localStorage to
  // enable the Home/Away team pickers for federation/competition orgs, so keep it in
  // sync with the in-memory selection whenever we drill into a group or gameday.
  const openGroup = (stage, group) => {
    setLocalStorageValue([{ id: storageId(localStorageIds.SELECTED_STAGE), value: stage }, { id: storageId(localStorageIds.SELECTED_GROUP), value: group }])
    setSelectedStage(stage); setSelectedGroup(group); setSelectedGameday(null); setShowSeasonsList(false)
  }
  const openGameday = (stage, group, gameday) => {
    setLocalStorageValue([{ id: storageId(localStorageIds.SELECTED_STAGE), value: stage }, { id: storageId(localStorageIds.SELECTED_GROUP), value: group }])
    setSelectedStage(stage); setSelectedGroup(group); setSelectedGameday(gameday); setShowSeasonsList(false)
  }

  /* ----------------------------------------------------------- games ------ */

  const openGameDrawer = (gameId, gameday) => {
    // Guarantee the event form sees the active group (it gates the team pickers on it).
    if (selectedGroup) setLocalStorageValue([{ id: storageId(localStorageIds.SELECTED_GROUP), value: selectedGroup }])
    const gameEventType = eventTypes?.data?.find((type) => type.code === EVENT_TYPE_FORMS.GAME)
    if (gameEventType) setSelectedEventType(gameEventType)
    const now = new Date()
    setDates({ start: new Date(now.getFullYear() - 1, 0, 1), end: new Date(now.getFullYear() + 1, 11, 31) })
    setGamedayId(gameday?.id || selectedGameday?.id || null)
    setClickedEventId(gameId || '')
    setCreateEventDrawerOpen(true)
  }
  const confirmDeleteGame = (game) =>
    setConfirm({
      title: 'Delete game',
      message: `Are you sure you want to delete the game "${game.title || ''}"?`,
      onConfirm: () => { deleteEvent.mutate({ eventTypeCode: 'game', eventId: game.id }, { onSuccess: invalidateGames }); closeConfirm() },
    })

  /* ----------------------------------------------------------- actions ---- */

  // Opens the Team Statistics page for a team on the season it was clicked from. The page keeps
  // this page's query (the org, and for a paired club its federation and competition).
  const openTeamStats = (team) => {
    const teamIdParam = team.teamId || team.seasonTeamId
    if (!teamIdParam) return
    const search = new URLSearchParams(location.search)
    if (seasonId) search.set('seasonId', seasonId)
    else search.delete('seasonId')
    history.push({ pathname: `/seasonTeams/${teamIdParam}`, search: `?${search}`, state: { seasonTeamId: team.seasonTeamId } })
  }

  const readActions = { selectSeason: openSeasonObj, openGame: openGameClickModal, openTeamStats }

  const actions = readOnly ? readActions : {
    ...readActions,
    // seasons
    newSeason: () => setSeasonAdd(true),
    editSeason: (season) => setSeasonEdit(season),
    refreshSeasonSync: (season) => refreshSeasonSyncMutation.mutate(season.id),
    refreshSeasonSyncLoading: refreshSeasonSyncMutation.isLoading,
    deleteSeason: (season) =>
      setConfirm({
        title: 'Delete season',
        message: `Delete "${season.name}"? This permanently removes its stages, groups, teams and games.`,
        onConfirm: () => deleteSeasonMutation.mutate(season.id),
      }),
    // stages
    createStage: () => setStageForm({}),
    editStage: (stage) => setStageForm({ stage }),
    deleteStage: (stage) =>
      setConfirm({ title: 'Delete stage', message: `Delete stage "${stage.name}" and everything inside it?`, onConfirm: () => deleteStageMutation.mutate(stage.id) }),
    // groups
    createGroup: (stage) => setGroupForm({ stageId: stage.id }),
    editGroup: (group) => setGroupForm({ group }),
    deleteGroup: (group) =>
      setConfirm({ title: 'Delete group', message: `Delete group "${group.name}" and its gamedays?`, onConfirm: () => deleteGroupMutation.mutate(group.id) }),
    // gamedays
    createGamedayBulk: (group) => setGamedayBulk({ group }),
    editGameday: (group, gameday) => setGamedayEdit({ group, gameday }),
    deleteGameday: (gameday) =>
      setConfirm({ title: 'Delete gameday', message: `Delete gameday "${gameday.name}" and its games?`, onConfirm: () => deleteGamedayMutation.mutate(gameday.id) }),
    // games
    createGame: (gameday) => openGameDrawer('', gameday),
    editGame: (gameId) => openGameDrawer(gameId, selectedGameday),
    deleteGame: confirmDeleteGame,
    // reordering (drag-and-drop)
    reorderStages: reorderStagesAction,
    reorderGroups: reorderGroupsAction,
    reorderGamedays: reorderGamedaysAction,
    // teams
    assignTeamSeasonButton: (
      <AssignTeamDropdown
        orgId={orgId}
        seasonId={seasonId}
        mode="season"
        gender={selectedCompetition?.gender}
        ageRestriction={selectedCompetition?.ageRestriction}
      />
    ),
    assignTeamGroupButton: (group) => <AssignTeamDropdown orgId={orgId} seasonId={seasonId} group={group} mode="group" />,
    editSeasonTeam: (team) => setEditingTeam(team),
    removeTeam: (team) =>
      setConfirm({ title: 'Remove team', message: `Remove "${team.teamName}" from this season?`, onConfirm: () => { removeFromSeasonMutation.mutate(team.seasonTeamId); closeConfirm() } }),
    removeTeamFromGroup: (group, team) =>
      setConfirm({ title: 'Remove team', message: `Remove "${team.teamName}" from ${group.name}?`, onConfirm: () => { removeFromGroupMutation.mutate({ groupId: group.id, seasonTeamId: team.seasonTeamId }); closeConfirm() } }),
    // competitions
    editCompetition: (competition) => setCompetitionForm({ competition }),
  }

  const nav = {
    openSeason: backToSeason,
    openStage,
    openGroup,
    openGameday,
  }

  /* -------------------------------------------------------- view select --- */

  let view
  if (!selectedCompetition) view = 'competitions'
  else if (showSeasonsList || !selectedSeason) view = 'competition'
  else if (selectedGameday) view = 'gameday'
  else if (selectedGroup) view = 'group'
  else if (selectedStage) view = 'stage'
  else view = 'season'

  const treeSelection = {
    competitionId: selectedCompetition?.id,
    seasonId: view === 'competition' ? null : selectedSeason?.id,
    stageId: selectedStage?.id,
    groupId: selectedGroup?.id,
    gamedayId: selectedGameday?.id,
  }

  // Competitions belong to the org: a Manager limited to some of them works inside them but neither
  // creates nor deletes one.
  const canManageCompetitions = !readOnly && hasPermission(USER_PERMISSIONS.SETUP_CMS, [orgId], { orgWide: true })
  const newCompetition = canManageCompetitions ? () => setCompetitionForm('new') : undefined

  const renderMain = () => {
    if (view === 'competitions') {
      return (
        <CompetitionsView
          competitions={competitions || []}
          isLoading={isLoadingCompetitions}
          onOpen={openCompetition}
          onCreate={newCompetition}
          onEdit={actions.editCompetition}
          onDelete={canManageCompetitions
            ? (c) => setConfirm({ title: 'Delete competition', message: `Delete "${c.name}"? This removes its seasons, stages, groups and games.`, onConfirm: () => deleteCompetitionMutation.mutate(c.id) })
            : undefined}
        />
      )
    }
    if (view === 'competition') {
      return (
        <CompetitionView
          competition={competitionDetail || selectedCompetition}
          seasons={competitionSeasons}
          isLoading={isLoadingCompetitionDetail}
          onOpenSeason={openSeasonObj}
          onNewSeason={actions.newSeason}
          onEditCompetition={actions.editCompetition}
          onEditSeason={actions.editSeason}
          onDeleteSeason={actions.deleteSeason}
        />
      )
    }
    if (view === 'season') {
      return (
        <SeasonView
          competition={selectedCompetition}
          season={selectedSeason}
          seasons={competitionSeasons}
          stages={stages}
          seasonTeams={seasonTeams}
          isLoadingStages={isLoadingStages}
          isLoadingTeams={isLoadingSeasonTeams}
          tab={seasonTab}
          setTab={setSeasonTab}
          nav={nav}
          actions={actions}
        />
      )
    }
    if (view === 'stage' && selectedStage) {
      return <StageView competition={selectedCompetition} season={selectedSeason} stage={selectedStage} nav={nav} actions={actions} />
    }
    if (view === 'group' && selectedGroup) {
      return <GroupView competition={selectedCompetition} season={selectedSeason} stage={selectedStage} group={selectedGroup} nav={nav} actions={actions} />
    }
    if (view === 'gameday' && selectedGameday) {
      return <GamedayView competition={selectedCompetition} season={selectedSeason} stage={selectedStage} group={selectedGroup} gameday={selectedGameday} nav={nav} actions={actions} />
    }
    return <div className={classes.loader}><CircularProgress style={{ color: TOKENS.coral }} /></div>
  }

  return (
    <>
      <div className={classes.shell}>
        <TournamentTree
          competitions={competitions || []}
          competitionDetail={competitionDetail}
          stages={stages}
          selection={treeSelection}
          onSelectCompetition={openCompetition}
          onSelectSeason={openSeasonObj}
          onSelectStage={openStage}
          onSelectGroup={openGroup}
          onSelectGameday={openGameday}
          onNewCompetition={newCompetition}
          onHome={goCompetitions}
        />
        <div className={classes.main}>{renderMain()}</div>
      </div>

      {!readOnly && (
      <>
      {/* ----------------------------------------------------- slide-overs -- */}
      <StageSlideOver
        open={Boolean(stageForm)}
        onClose={() => setStageForm(null)}
        orgId={orgId}
        seasonId={seasonId}
        stage={stageForm?.stage}
      />
      <GroupSlideOver
        open={Boolean(groupForm)}
        onClose={() => setGroupForm(null)}
        orgId={orgId}
        seasonId={seasonId}
        stageId={groupForm?.stageId || selectedStage?.id}
        group={groupForm?.group}
      />

      {/* ------------------------------------------------- existing modals -- */}
      <SeasonSlideOver
        open={seasonAdd || Boolean(seasonEdit)}
        onClose={() => { setSeasonAdd(false); setSeasonEdit(null) }}
        orgId={orgId}
        competitionId={selectedCompetition?.id}
        season={seasonEdit}
        onCreated={openSeasonObj}
      />
      <CompetitionSlideOver
        open={Boolean(competitionForm)}
        onClose={() => setCompetitionForm(null)}
        orgId={orgId}
        competition={competitionForm === 'new' ? null : competitionForm?.competition}
      />
      {gamedayBulk && (
        <BulkAddGamedaysModal
          isOpen
          onClose={() => setGamedayBulk(null)}
          orgId={orgId}
          seasonId={seasonId}
          groupId={gamedayBulk.group.id}
        />
      )}
      {gamedayEdit && (
        <EditGamedayModal
          isOpen
          onClose={() => setGamedayEdit(null)}
          orgId={orgId}
          seasonId={seasonId}
          groupId={gamedayEdit.group.id}
          gameday={gamedayEdit.gameday}
        />
      )}
      <EditSeasonTeamModal
        open={Boolean(editingTeam)}
        team={editingTeam}
        onClose={() => setEditingTeam(null)}
        onConfirm={(name) => updateSeasonTeamMutation.mutate({ seasonTeamId: editingTeam.seasonTeamId, name })}
        isLoading={updateSeasonTeamMutation.isLoading}
      />

      {/* ----------------------------------------------- confirm dialog ----- */}
      <Dialog open={Boolean(confirm)} onClose={closeConfirm}>
        <DialogTitle>{confirm?.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirm?.message}</Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={closeConfirm}>Cancel</MuiButton>
          <MuiButton onClick={() => confirm?.onConfirm?.()} color="secondary" variant="contained" disabled={anyDeleteLoading || deleteEvent.isLoading}>
            {anyDeleteLoading || deleteEvent.isLoading ? <CircularProgress size={22} /> : 'Delete'}
          </MuiButton>
        </DialogActions>
      </Dialog>

      <CreateEventDrawer />

      <Snackbar
        open={syncStartedOpen}
        autoHideDuration={5000}
        onClose={() => setSyncStartedOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSyncStartedOpen(false)} severity="success" variant="filled">
          Sync started. The season structure and upcoming games are refreshing in the background.
        </Alert>
      </Snackbar>
      </>
      )}

      <CalendarEventClickModal
        open={eventClickModalOpen}
        handleClose={() => setEventClickModalOpen(false)}
        onEdit={readOnly ? undefined : (gameId) => openGameDrawer(gameId, selectedGameday)}
        onDelete={readOnly ? undefined : invalidateGames}
        paired={readOnly ? { orgId, competitionId } : undefined}
      />
    </>
  )
}

const CmsAdminPageFederation = () => (
  <TeamCalendarProvider teamsSource="season" requireSeason>
    <CmsAdminPageContent />
  </TeamCalendarProvider>
)

export default CmsAdminPageFederation
